import { type Checkout, CheckoutStatus, type Permission, ReservationStatus } from '@cheqroom/api';
import { DateTime } from '@cheqroom/date-time';
import { useMemo } from 'react';

import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';
import { createUserPermissions } from './use-user-permissions.hook';
import { createSpotcheckPermissions } from './use-spotcheck-permissions.hook';

interface Options {
	isExtending?: boolean;
	own?: boolean;
}

interface FeatureFlags {
	allowPastBookings?: boolean;
}

type UseCheckoutPermissionsProps = PermissionsProps & FeatureFlags;

const MINIMUM_DATETIME_FOR_CONSIDERED_IN_PAST = { hours: 1 };

/** The person a booking is for: a contact id plus its status (only active contacts can be booked for). */
export type BookingCustomer = { _id: string; status: string | null | undefined };

/**
 * Who a booking may be for, over a booking hook's writer right (`own`: the own tier counts) and its
 * book-for-others rule (`setCustomer`).
 * Shared by check-outs and reservations so both follow the same rule.
 */
export const createBookingCustomerRules = (
	canWriter: (own?: boolean) => boolean | undefined,
	bookForOthers: () => boolean,
	permissions: ReturnType<typeof usePermissions>
) => ({
	/** Book for this person: yourself on the own tier, anybody else only with `setCustomer`. Active contacts only. */
	canBookFor: (customer: BookingCustomer): boolean =>
		!!canWriter(true) && customer.status === 'active' && (permissions.isOwn(customer._id) || bookForOthers()),
	/** Change who a booking is for: needs `setCustomer` and the users list to pick from. */
	canChangeBookingUser: (): boolean => bookForOthers() && !!createUserPermissions(permissions).canRead(),
});

export const createCheckoutPermissions = (
	permissions: ReturnType<typeof usePermissions>,
	featureFlags?: FeatureFlags
) => {
	const spotcheckPermissions = createSpotcheckPermissions(permissions);

	const { allowPastBookings = false } = featureFlags ?? {};
	const {
		hasAnyPermission: _hasAnyPermission,
		useCheckouts,
		orderCheckinLocation,
		usePastOrders,
		useBlockEditOfOpenOrders,
		usePdf,
		useSpotchecks,
		canUpgrade,
		useExport,
	} = permissions;

	const hasAnyPermission = (...required: Permission[]) => useCheckouts && _hasAnyPermission(...required);

	const canWriter = (own?: boolean) =>
		hasAnyPermission('ORDERS_WRITER', 'ORDERS_WRITER_RESTRICTED') || (own && hasAnyPermission('ORDERS_OWN_WRITER'));

	/** Booking for somebody else (`setCustomer`), as python-api `create` decides it. */
	const bookForOthers = (): boolean =>
		!!hasAnyPermission('ORDERS_WRITER_RESTRICTED') ||
		(!!hasAnyPermission('ORDERS_WRITER') && !hasAnyPermission('ORDERS_OWN_WRITER'));
	const bookingCustomerRules = createBookingCustomerRules(canWriter, bookForOthers, permissions);

	// python-api `checkout` / `checkoutAgain` need ORDERS_CHECKOUTER*; the writer right adds the equipment.
	const canCheckouter = (own?: boolean) =>
		hasAnyPermission('ORDERS_CHECKOUTER', 'ORDERS_CHECKOUTER_RESTRICTED') ||
		(own && hasAnyPermission('ORDERS_OWN_CHECKOUTER'));
	const canCheckoutBooking = (own?: boolean): boolean => !!canWriter(own) && !!canCheckouter(own);

	const canArchive = (own?: boolean) =>
		hasAnyPermission('ORDERS_ARCHIVER', 'ORDERS_ARCHIVER_RESTRICTED') ||
		(own && hasAnyPermission('ORDERS_OWN_ARCHIVER'));

	const canLabel = (own?: boolean) =>
		hasAnyPermission('ORDERS_LABELER', 'ORDERS_LABELER_RESTRICTED') ||
		(own && hasAnyPermission('ORDERS_OWN_LABELER'));

	const checkoutPermissions = {
		canCreate: () => canWriter(true),
		canCheckout: canCheckoutBooking,
		// Picking who a check-out is for (`setCustomer`): the own tier never covers somebody else.
		// python-api `create`: with ORDERS_OWN_WRITER and no ORDERS_WRITER_RESTRICTED, the booking is for yourself.
		canCheckoutForSomeoneElse: bookForOthers,
		canCheckoutFor: bookingCustomerRules.canBookFor,
		canChangeCheckoutUser: bookingCustomerRules.canChangeBookingUser,
		canUpdate: canWriter,
		// `own` must be passed for the own tier to count; no default.
		canRead: (own?: boolean) =>
			hasAnyPermission('ORDERS_READER', 'ORDERS_READER_RESTRICTED') ||
			(!!own && hasAnyPermission('ORDERS_OWN_READER')),
		canOverrideConflicts: () => hasAnyPermission('ORDERS_CONFLICT_CREATOR'),
		canCheckin: (checkout: Pick<Checkout, 'status'>, own?: boolean) =>
			checkout.status === CheckoutStatus.OPEN &&
			(hasAnyPermission('ORDERS_CHECKINNER', 'ORDERS_CHECKINNER_RESTRICTED') ||
				(own && hasAnyPermission('ORDERS_OWN_CHECKINNER'))),
		canExtendCheckout: (checkout: Pick<Checkout, 'status'>, own?: boolean) =>
			checkout.status === CheckoutStatus.OPEN &&
			(hasAnyPermission('ORDERS_EXTENDER', 'ORDERS_EXTENDER_RESTRICTED') ||
				(own && hasAnyPermission('ORDERS_OWN_EXTENDER'))),
		canCheckinAtDifferentLocation: (checkout: Pick<Checkout, 'status'>, own?: boolean) =>
			!!orderCheckinLocation && checkoutPermissions.canCheckin(checkout, own),
		canSelectEquipment: (checkout: Pick<Checkout, 'status'>, options: Options) =>
			(checkout.status === CheckoutStatus.CREATING || checkout.status === CheckoutStatus.OPEN) &&
			!options.isExtending &&
			canWriter(options.own),
		canWritePast: (own?: boolean) => {
			if (!usePastOrders || !allowPastBookings) return false;
			return hasAnyPermission('ORDERS_WRITER_PAST') && canWriter(own);
		},
		canUndoFutureBooking: (checkout: Pick<Checkout, 'status'>, own?: boolean) =>
			checkout.status === CheckoutStatus.OPEN && canWriter(own),
		canUndoPastBooking: (checkout: Pick<Checkout, 'status'>, own?: boolean) =>
			checkoutPermissions.canUndoFutureBooking(checkout, own) && checkoutPermissions.canWritePast(own),
		canUndoCurrentBooking: (checkout: Pick<Checkout, 'status' | 'started'>, own?: boolean) => {
			if (checkout.status !== CheckoutStatus.OPEN) return false;
			// Check if user has permission to undo bookings
			if (!canWriter(own)) return false;

			// If booking hasn't started yet (not in past), can always undo
			const isFromDateInPast =
				checkout.started &&
				DateTime.fromISOString(checkout.started).isBefore(
					DateTime.now().subtract(MINIMUM_DATETIME_FOR_CONSIDERED_IN_PAST)
				);
			if (!isFromDateInPast) {
				return true;
			}
			// For bookings that have already started (in past):
			// If past bookings are disabled OR blocking is disabled, always allow undo
			const usePastBookings = !!usePastOrders;
			const blockEditOfOpenBookings = !!useBlockEditOfOpenOrders;
			const alwaysAllowUndo = !usePastBookings || !blockEditOfOpenBookings;
			if (alwaysAllowUndo) {
				return true;
			}
			// If past bookings are enabled AND blocking is enabled:
			// Need write-past permission to undo past bookings
			return checkoutPermissions.canWritePast(own);
		},
		canEdit: (checkout: Pick<Checkout, 'status'>, own?: boolean) =>
			checkout.status === CheckoutStatus.OPEN && canWriter(own),
		canEditFields: (checkout: Pick<Checkout, 'archived'>, own?: boolean) => !checkout.archived && canWriter(own),
		canDelete: (checkout: Pick<Checkout, 'status'>, own?: boolean) =>
			checkout.status === CheckoutStatus.CREATING && canWriter(own),
		canBookAgain: canCheckoutBooking,
		canRepeatBooking: canWriter,
		canArchive: (checkout: Pick<Checkout, 'status' | 'archived'>, own?: boolean) =>
			checkout.status === CheckoutStatus.CLOSED && !checkout.archived && canArchive(own),
		canUndoArchive: (checkout: Pick<Checkout, 'archived'>, own?: boolean) => !!checkout.archived && canArchive(own),
		canGenerateDocument: (own?: boolean) =>
			!!usePdf &&
			(hasAnyPermission('ORDERS_DOCUMENT_GENERATOR', 'ORDERS_DOCUMENT_GENERATOR_RESTRICTED') ||
				(own && hasAnyPermission('ORDERS_OWN_DOCUMENT_GENERATOR'))),
		canUpgradePlan: canUpgrade,
		canCreateSpotcheck: () => !!useSpotchecks && spotcheckPermissions.canCreate(),
		canReadBooking: (own?: boolean) => checkoutPermissions.canRead(own),
		canAddAttachment: (own?: boolean) =>
			hasAnyPermission('ORDERS_ATTACHMENTS_OWN_WRITER') ||
			(!!own && hasAnyPermission('ORDERS_OWN_ATTACHMENTS_OWN_WRITER')),
		/** `ownAttachment` / `ownComment` (it is yours) first, then `ownBooking`, as python-api and legacy. */
		canRemoveAttachment: (ownAttachment?: boolean, ownBooking?: boolean) =>
			hasAnyPermission('ORDERS_ATTACHMENTS_DELETER') ||
			(!!ownAttachment && hasAnyPermission('ORDERS_ATTACHMENTS_OWN_DELETER')) ||
			(!!ownAttachment && !!ownBooking && hasAnyPermission('ORDERS_OWN_ATTACHMENTS_OWN_DELETER')),
		/** `ownBooking`: the check-out is the signed-in user's. */
		canAddComment: (ownBooking?: boolean) =>
			hasAnyPermission('ORDERS_COMMENTS_OWN_WRITER') ||
			(!!ownBooking && hasAnyPermission('ORDERS_OWN_COMMENTS_OWN_WRITER')),
		canEditComment: (ownComment?: boolean, ownBooking?: boolean) =>
			(!!ownComment && hasAnyPermission('ORDERS_COMMENTS_OWN_WRITER')) ||
			(!!ownComment && !!ownBooking && hasAnyPermission('ORDERS_OWN_COMMENTS_OWN_WRITER')),
		canRemoveComment: (ownComment?: boolean, ownBooking?: boolean) =>
			hasAnyPermission('ORDERS_COMMENTS_DELETER') ||
			(!!ownComment && hasAnyPermission('ORDERS_COMMENTS_OWN_DELETER')) ||
			(!!ownComment && !!ownBooking && hasAnyPermission('ORDERS_OWN_COMMENTS_OWN_DELETER')),
		canExport: () => !!useExport && hasAnyPermission('ORDERS_EXPORTER', 'ORDERS_EXPORTER_RESTRICTED'),
		canSetLabel: (checkout: Pick<Checkout, 'archived'>, own?: boolean) => !checkout.archived && canLabel(own),
		canClearLabel: (checkout: Pick<Checkout, 'archived'>, own?: boolean) => !checkout.archived && canLabel(own),
	};

	return checkoutPermissions;
};

export const useCheckoutPermissions = ({ allowPastBookings = false, ...props }: UseCheckoutPermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(
		() => createCheckoutPermissions(permissions, { allowPastBookings }),
		[permissions, allowPastBookings]
	);
};
